import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getDashboardOverview } from './dashboard-repository';
import { formatCurrency, formatShortDate, selectTaskChartSegments } from './dashboard-selectors';
import type { DashboardOverviewRecord } from '@shared/types';
import { EmptyState, MetricCard, PanelCard, PanelMenuButton, SectionHeader, StatusBadge } from '@renderer/shared/components/primitives';
import { Icon } from '@renderer/shared/components/icons';
import { useI18n } from '@renderer/shared/i18n';

export function DashboardScreen(): JSX.Element {
  const { locale, t } = useI18n();
  const [overview, setOverview] = useState<DashboardOverviewRecord | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;

    void getDashboardOverview()
      .then((nextOverview) => {
        if (active) {
          setOverview(nextOverview);
        }
      })
      .catch((nextError) => {
        if (active) {
          setError(nextError instanceof Error ? nextError.message : 'Dashboard unavailable.');
        }
      });

    return () => {
      active = false;
    };
  }, []);

  if (error) {
    return (
      <div className="av-page">
        <EmptyState title={t('dashboard.title')} description={error} />
      </div>
    );
  }

  if (!overview) {
    return <div className="av-page" />;
  }

  const segments = selectTaskChartSegments(overview, t);

  return (
    <div className="av-page">
      <SectionHeader title={t('dashboard.title')} subtitle={t('dashboard.subtitle')} />

      <div className="av-metric-grid">
        <MetricCard icon="work" label={t('dashboard.kpis.activeProjects')} value={overview.kpis.activeProjects} />
        <MetricCard icon="customers" label={t('dashboard.kpis.activeCustomers')} value={overview.kpis.activeCustomers} />
        <MetricCard icon="invoices" label={t('dashboard.kpis.dueInvoices')} value={overview.kpis.dueInvoices} />
        <MetricCard icon="contracts" label={t('dashboard.kpis.activeContracts')} value={overview.kpis.activeContracts} />
      </div>

      <div className="av-dashboard-grid">
        <PanelCard actions={<PanelMenuButton />} className="av-dashboard-grid__wide" title={t('dashboard.panels.recentActivity')}>
          <ul className="av-activity-list">
            {overview.recentActivity.map((item) => (
              <li key={item.id} className="av-activity-list__item">
                <Icon name="document" />
                <div>
                  <strong>{t(`dashboard.activity.${item.kind}`, { actorName: item.actorName, subject: item.subject })}</strong>
                  <span>{new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(item.occurredAt))}</span>
                </div>
              </li>
            ))}
          </ul>
        </PanelCard>

        <PanelCard actions={<PanelMenuButton />} title={t('dashboard.panels.financialOverview')}>
          <div className="av-financial-total">
            <span>{t('dashboard.financial.issuedThisMonth')}</span>
            <strong>{formatCurrency(locale, overview.financialOverview.issuedThisMonth, overview.financialOverview.currency)}</strong>
          </div>
          <div className="av-financial-bars">
            <div>
              <div className="av-financial-bars__row">
                <span>{t('dashboard.financial.collected')}</span>
                <strong>{formatCurrency(locale, overview.financialOverview.collectedAmount, overview.financialOverview.currency)}</strong>
              </div>
              <div className="av-progress">
                <div className="av-progress__fill av-progress__fill--success" style={{ width: '78%' }} />
              </div>
            </div>
            <div>
              <div className="av-financial-bars__row">
                <span>{t('dashboard.financial.outstanding')}</span>
                <strong>{formatCurrency(locale, overview.financialOverview.outstandingAmount, overview.financialOverview.currency)}</strong>
              </div>
              <div className="av-progress">
                <div className="av-progress__fill av-progress__fill--warning" style={{ width: '42%' }} />
              </div>
            </div>
          </div>
        </PanelCard>

        <PanelCard actions={<Link className="av-panel-link" to="/payments">{t('common.seeAll')}</Link>} title={t('dashboard.panels.payments')}>
          <div className="av-financial-total">
            <span>{t('dashboard.payments.pending')}</span>
            <strong>
              {formatCurrency(locale, overview.paymentsOverview.pendingAmount / 100, overview.paymentsOverview.currency)}
            </strong>
          </div>
          <div className="av-payment-dashboard-meta">
            <StatusBadge label={t('dashboard.payments.pendingCount', { count: overview.paymentsOverview.pendingCount })} tone="primary" />
            <StatusBadge label={t('dashboard.payments.paidCount', { count: overview.paymentsOverview.paidCount })} tone="success" />
          </div>
          <ul className="av-compact-list">
            {overview.paymentsOverview.recent.map((paymentOrder) => (
              <li key={paymentOrder.id} className="av-compact-list__item">
                <div>
                  <strong>{paymentOrder.title}</strong>
                  <span className="av-muted-line">{paymentOrder.customerName}</span>
                </div>
                <StatusBadge label={t(`enums.paymentStatus.${paymentOrder.status}`)} tone={paymentOrder.status === 'paid' ? 'success' : 'neutral'} />
              </li>
            ))}
          </ul>
        </PanelCard>

        <PanelCard actions={<button className="av-panel-link" type="button">{t('common.seeAll')}</button>} title={t('dashboard.panels.activeTasks')}>
          <ul className="av-compact-list">
            {overview.activeTasks.map((task) => (
              <li key={task.id} className="av-compact-list__item">
                <div>
                  <strong>{task.title}</strong>
                </div>
                {task.dueAt ? <StatusBadge label={new Intl.RelativeTimeFormat(locale, { numeric: 'auto' }).format(Math.ceil((new Date(task.dueAt).getTime() - Date.now()) / (1000 * 60 * 60 * 24)), 'day')} tone="neutral" /> : null}
              </li>
            ))}
          </ul>
        </PanelCard>

        <PanelCard actions={<button className="av-panel-link" type="button">{t('common.seeAll')}</button>} title={t('dashboard.panels.upcomingDeadlines')}>
          <ul className="av-deadline-list">
            {overview.upcomingDeadlines.map((deadline) => (
              <li key={deadline.id} className="av-deadline-list__item">
                <Icon name={deadline.kind === 'invoice' ? 'document' : deadline.kind === 'project' ? 'work' : 'contracts'} />
                <div>
                  <strong>{deadline.title}</strong>
                  <span>{formatShortDate(locale, deadline.dueAt)}</span>
                </div>
              </li>
            ))}
          </ul>
        </PanelCard>

        <PanelCard actions={<PanelMenuButton />} title={t('dashboard.panels.taskOverview')}>
          <div className="av-task-chart">
            <div className="av-task-chart__summary">{overview.taskStatusOverview.total}</div>
            <ul className="av-task-chart__legend">
              {segments.map((segment) => (
                <li key={segment.status}>
                  <span className={`av-dot av-dot--${segment.status}`} />
                  <span>{segment.label}</span>
                  <strong>{segment.count}</strong>
                </li>
              ))}
            </ul>
          </div>
        </PanelCard>
      </div>
    </div>
  );
}
